import React, { useMemo, useState } from 'react';
import { Check } from 'lucide-react';

/**
 * Shaft catalog picker: brand tabs + shaft cards with image, launch/spin badges, price.
 * Matches the look of FairwayJockey's shaft selector on a driver build page.
 */
export default function ShaftPicker({ shafts, value, onChange, required, testId, catalogShaftOverrides }) {
  const overrides = catalogShaftOverrides || {};

  // Effective price for a shaft = override if present, else the shaft's catalog MAP
  const effectivePrice = React.useCallback(
    (s) => {
      const ov = overrides[s.product_id];
      return ov === undefined || ov === null ? Number(s.min_advertised_price || 0) : Number(ov);
    },
    [overrides]
  );

  const isNoUpcharge = React.useCallback(
    (s) => effectivePrice(s) === 0,
    [effectivePrice]
  );

  const noUpchargeCount = useMemo(
    () => (shafts || []).filter((s) => isNoUpcharge(s)).length,
    [shafts, isNoUpcharge]
  );

  const brands = useMemo(() => {
    const set = new Set();
    for (const s of shafts || []) if (s.manufacturer) set.add(s.manufacturer);
    return Array.from(set).sort();
  }, [shafts]);

  const [brand, setBrand] = useState('All');
  const [hovered, setHovered] = useState(null);
  const [hoverRect, setHoverRect] = useState(null);

  const handleEnter = (shaft, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setHovered(shaft);
    setHoverRect(rect);
  };
  const handleLeave = (shaft) => {
    setHovered((h) => (h === shaft ? null : h));
  };

  const filtered = useMemo(() => {
    if (!shafts) return [];
    if (brand === 'No Upcharge') return shafts.filter((s) => isNoUpcharge(s));
    if (brand === 'All') return shafts;
    return shafts.filter((s) => s.manufacturer === brand);
  }, [shafts, brand, isNoUpcharge]);

  const selected = shafts?.find((s) => s.product_id === value);

  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-[#334155] uppercase tracking-widest">
          Shaft {required && <span className="text-[#D4A55A] ml-1">*</span>}
        </span>
        {selected && (
          <span className="text-xs text-gray-600">
            Selected: <span className="font-semibold text-gray-900">{selected.name}</span>
          </span>
        )}
      </div>

      {/* Brand tabs */}
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="shaft-brand-tabs">
        <button
          type="button"
          onClick={() => setBrand('All')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
            brand === 'All'
              ? 'bg-[#334155] text-white border-[#334155]'
              : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
          }`}
        >
          All ({shafts?.length || 0})
        </button>
        {noUpchargeCount > 0 && (
          <button
            type="button"
            onClick={() => setBrand('No Upcharge')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              brand === 'No Upcharge'
                ? 'bg-emerald-700 text-white border-emerald-700'
                : 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:border-emerald-500'
            }`}
            data-testid="shaft-brand-no-upcharge"
          >
            No Upcharge ({noUpchargeCount})
          </button>
        )}
        {brands.map((b) => (
          <button
            key={b}
            type="button"
            onClick={() => setBrand(b)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              brand === b
                ? 'bg-[#334155] text-white border-[#334155]'
                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
            }`}
            data-testid={`shaft-brand-${b.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {b}
          </button>
        ))}
      </div>

      {/* Shaft grid */}
      <div
        className={`grid grid-cols-1 gap-2 max-h-[460px] overflow-y-auto pr-1 border rounded-lg p-2 bg-gray-50 ${
          required && !value ? 'border-[#D4A55A]' : 'border-[#F3F4F6]'
        }`}
      >
        {filtered.map((shaft) => {
          const isSelected = shaft.product_id === value;
          const sc = shaft.shaft_characteristics || {};
          const eff = effectivePrice(shaft);
          const nou = isNoUpcharge(shaft);
          return (
            <button
              key={shaft.product_id}
              type="button"
              onClick={() => onChange(shaft.product_id)}
              onMouseEnter={(e) => handleEnter(shaft, e)}
              onMouseLeave={() => handleLeave(shaft)}
              className={`group relative flex items-center gap-3 p-2 rounded-md border text-left transition-all ${
                isSelected
                  ? 'border-[#334155] bg-white ring-2 ring-[#334155]/20'
                  : nou
                    ? 'border-emerald-200 bg-white hover:border-emerald-500'
                    : 'border-gray-200 bg-white hover:border-gray-400'
              }`}
              data-testid={`shaft-option-${shaft.product_id}`}
            >
              <div className="flex-shrink-0 w-24 h-14 bg-gray-100 rounded overflow-hidden flex items-center justify-center">
                {shaft.image_url ? (
                  <img
                    src={shaft.image_url}
                    alt={shaft.name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-[10px] text-gray-400">No image</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  {shaft.manufacturer}
                </p>
                <p className="text-sm font-medium text-gray-900 leading-tight line-clamp-2">
                  {shaft.name}
                </p>
                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  {sc.launch && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100">
                      {sc.launch} Launch
                    </span>
                  )}
                  {sc.spin && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100">
                      {sc.spin} Spin
                    </span>
                  )}
                  <span className="text-[11px] ml-auto">
                    {nou ? (
                      <span className="font-semibold text-emerald-700">+$0</span>
                    ) : (
                      <span className="text-gray-600">+${eff.toFixed(0)}</span>
                    )}
                  </span>
                </div>
              </div>
              {nou && !isSelected && (
                <div className="absolute -top-1 -right-1 bg-emerald-600 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-full uppercase tracking-wider shadow-sm">
                  No Upcharge
                </div>
              )}
              {isSelected && (
                <div className="absolute top-1.5 right-1.5 bg-[#334155] text-white rounded-full p-0.5">
                  <Check className="w-3 h-3" />
                </div>
              )}
            </button>
          );
        })}
        {filtered.length === 0 && (
          <p className="text-center text-sm text-gray-500 py-6">
            No shafts in this brand.
          </p>
        )}
      </div>

      {/* Floating hover preview - anchored next to hovered card */}
      {hovered && hovered.image_url && hoverRect && (() => {
        const W = 420;
        const gap = 16;
        let left = hoverRect.right + gap;
        if (left + W > window.innerWidth - 8) {
          left = hoverRect.left - W - gap;
        }
        const top = Math.max(8, Math.min(hoverRect.top, window.innerHeight - 500));
        return (
          <div
            className="fixed z-50 pointer-events-none animate-in fade-in duration-150"
            style={{ top, left, width: W }}
            data-testid="shaft-hover-preview"
          >
            <div className="bg-white border border-gray-200 rounded-xl shadow-2xl p-5">
              <img
                src={hovered.image_url}
                alt={hovered.name}
                className="w-full h-80 object-contain rounded"
              />
              <p className="mt-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                {hovered.manufacturer}
              </p>
              <p className="text-sm font-semibold text-gray-900 leading-tight">
                {hovered.name}
              </p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {hovered.shaft_characteristics?.launch && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100">
                    {hovered.shaft_characteristics.launch} Launch
                  </span>
                )}
                {hovered.shaft_characteristics?.spin && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100">
                    {hovered.shaft_characteristics.spin} Spin
                  </span>
                )}
                <span className="text-xs text-[#334155] ml-auto font-semibold">
                  {isNoUpcharge(hovered) ? (
                    <span className="text-emerald-700">No Upcharge</span>
                  ) : (
                    <>+${effectivePrice(hovered).toFixed(0)}</>
                  )}
                </span>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
